export const GAME_METADATA = {
  id: 'rogue-brick-breaker',
  title: 'Rogue Brick Breaker',
  logicalWidth: 720,
  logicalHeight: 1280,
} as const;

export const OBJECTIVE_DEFINITIONS: Readonly<Record<ObjectiveId, ObjectiveDefinition>> = {
  'active-operator': {
    id: 'active-operator',
    kind: 'mission',
    metric: 'ability-uses',
    name: '主动干预',
    description: '在正式对局中累计释放 5 次主动技能。',
    target: 5,
    progressLabel: '技能释放',
    rewardLabel: '档案徽章 · 战术操作员',
  },
  'boss-interceptor': {
    id: 'boss-interceptor',
    kind: 'mission',
    metric: 'bosses-defeated',
    name: '首领截击',
    description: '累计击破 3 个第 10、20 或 30 关 Boss。',
    target: 3,
    progressLabel: 'Boss 击破',
    rewardLabel: '档案徽章 · 虚空猎手',
  },
  'event-cartographer': {
    id: 'event-cartographer',
    kind: 'mission',
    metric: 'events-resolved',
    name: '事件测绘',
    description: '累计完成 3 次局间事件决策，安全离开同样算一次探索。',
    target: 3,
    progressLabel: '事件决策',
    rewardLabel: '档案徽章 · 路线测绘员',
  },
  'fusion-engineer': {
    id: 'fusion-engineer',
    kind: 'achievement',
    metric: 'fusion-activated',
    name: '融合工程',
    description: '在一局结束时拥有至少一种球芯融合。',
    target: 1,
    progressLabel: '融合构筑',
    rewardLabel: '成就徽章 · 双核工程师',
  },
  'curse-breaker': {
    id: 'curse-breaker',
    kind: 'achievement',
    metric: 'curses-cleansed',
    name: '断咒协议',
    description: '在稳定净化站主动净化一次诅咒。',
    target: 1,
    progressLabel: '主动净化',
    rewardLabel: '成就徽章 · 稳定执行者',
  },
  'thirty-wave-victor': {
    id: 'thirty-wave-victor',
    kind: 'achievement',
    metric: 'full-runs-won',
    name: '三十关远征',
    description: '从第 1 关出发并完整赢得全部 30 关。',
    target: 1,
    progressLabel: '完整通关',
    rewardLabel: '成就徽章 · 奇点征服者',
  },
};

export const SEASON_CHALLENGE_DEFINITIONS: Readonly<
  Record<SeasonChallengeId, SeasonChallengeDefinition>
> = {
  'skill-circuit': {
    id: 'skill-circuit',
    metric: 'ability-uses',
    name: '脉冲回路',
    description: '在本周正式对局中累计释放 8 次主动技能。',
    target: 8,
    progressLabel: '技能释放',
    rewardLabel: '赛季徽记 · 脉冲操作员',
    actionPath: '/levels',
  },
  'boss-breach': {
    id: 'boss-breach',
    metric: 'bosses-defeated',
    name: '核心突破',
    description: '在本周正式对局中累计击破 2 个 Boss。',
    target: 2,
    progressLabel: 'Boss 击破',
    rewardLabel: '赛季徽记 · 核心破阵者',
    actionPath: '/levels',
  },
  'event-expedition': {
    id: 'event-expedition',
    metric: 'events-resolved',
    name: '事件远征',
    description: '在本周累计完成 3 次局间事件决策，安全离开同样计数。',
    target: 3,
    progressLabel: '事件决策',
    rewardLabel: '赛季徽记 · 深空测绘员',
    actionPath: '/levels',
  },
  'fusion-trial': {
    id: 'fusion-trial',
    metric: 'completed-fusion-runs',
    name: '双核试炼',
    description: '以胜利或失败正常结束一局，并在结算时拥有至少一种球芯融合。',
    target: 1,
    progressLabel: '融合结算',
    rewardLabel: '赛季徽记 · 双核先驱',
    actionPath: '/levels',
  },
};

export const SEASON_DEFINITIONS: Readonly<Record<SeasonId, SeasonDefinition>> = {
  'voidline-s1': {
    id: 'voidline-s1',
    version: 1,
    name: '虚线回声 · 第一赛季',
    description: '四周轮换协议，记录战术广度与构筑实验，不提供永久数值加成。',
    startsAt: '2026-08-17T00:00:00.000Z',
    endsAt: '2026-09-14T00:00:00.000Z',
    rotationDurationDays: 7,
    challengeIds: ['skill-circuit', 'boss-breach', 'event-expedition', 'fusion-trial'],
  },
};

export const GAMEPLAY_CONFIG = {
  lives: 3,
  physicsFps: 120,
  playfield: {
    top: 190,
    bossTop: 300,
    bottom: 1080,
    aegisY: 1068,
  },
  paddle: {
    width: 156,
    height: 24,
    y: 1035,
    speed: 900,
  },
  ball: {
    radius: 14,
    speed: 640,
  },
  projectiles: {
    softLimit: 48,
    hardLimit: 96,
    splitCount: 24,
    stressCount: 72,
    minimumVerticalSpeedRatio: 0.38,
    minimumHorizontalSpeedRatio: 0.18,
    baseDamage: 1,
    splitDamageMultiplier: 0.45,
    minimumDamage: 0.2,
  },
  bricks: {
    rows: 5,
    columns: 7,
    width: 78,
    height: 34,
    horizontalGap: 12,
    verticalGap: 14,
    top: 260,
    score: 100,
    hitPoints: 1,
  },
  effects: {
    impactPoolSize: 24,
    breakShardPoolSize: 72,
  },
  abilities: {
    ionPulseBrickDamage: 1,
    ionPulseBossDamage: 12,
  },
  run: {
    maxWaves: 30,
    upgradeChoiceCount: 3,
  },
  boss: {
    waves: [10, 20, 30],
    rewardWaves: [10, 20],
    hitPointsByTier: [240, 720, 1_800],
    movementSpeedMultipliers: [1, 1.22, 1.48],
    verticalAmplitudeMultipliers: [1, 1.2, 1.45],
    y: 430,
    firstEncounterShield: {
      nodeCount: 2,
      nodeHitPoints: 12,
      vulnerableDurationMilliseconds: 8_000,
      nodeHorizontalOffset: 182,
      nodeVerticalOffset: 6,
      nodeSize: 44,
    },
    secondEncounterSummon: {
      capacity: 4,
      reinforcementHitPoints: 8,
      initialDelayMilliseconds: 3_000,
      summonIntervalsByPhase: [8_000, 6_500, 5_000],
      bossDamageReductionPerReinforcement: 0.18,
      minimumBossDamageMultiplier: 0.28,
      reinforcementSize: 48,
      formationHorizontalOffset: 190,
      formationVerticalOffset: 82,
    },
    thirdEncounterSingularity: {
      warningDurationMilliseconds: 2_200,
      activeDurationMilliseconds: 3_000,
      cooldownMillisecondsByPhase: [7_200, 6_000, 4_800],
      steeringRadiansPerSecondByPhase: [0.24, 0.3, 0.36],
      aegisSteeringMultiplier: 0.2,
      anchorHorizontalOffset: 200,
      anchorY: 700,
      visualRadius: 62,
    },
  },
  elite: {
    waves: [5, 15, 25],
  },
  contracts: {
    offerWaves: [10, 20],
  },
  events: {
    offerWaves: [6, 16, 26],
  },
  cleanses: {
    offerWaves: [7, 11, 17, 21, 27],
  },
} as const;

export const BOSS_DEFINITION: BossDefinition = {
  id: 'singularity-warden',
  name: '奇点守卫',
  maxHitPoints: GAMEPLAY_CONFIG.boss.hitPointsByTier[0],
  width: 300,
  height: 104,
  phaseThresholds: [0.66, 0.33],
  phases: [
    {
      id: 1,
      name: '警戒',
      description: '守卫在上层区域缓慢巡航',
      movementSpeed: 70,
      tint: 0x5eead4,
    },
    {
      id: 2,
      name: '过载',
      description: '机动速度提升，开始纵向摆动',
      movementSpeed: 130,
      tint: 0x818cf8,
    },
    {
      id: 3,
      name: '奇点',
      description: '守卫进入高速不稳定状态',
      movementSpeed: 205,
      tint: 0xf472b6,
    },
  ],
};

export const BOSS_AFFIX_DEFINITIONS: Readonly<Record<BossAffixId, BossAffixSnapshot>> = {
  'armored-core': {
    id: 'armored-core',
    name: '装甲核心',
    description: 'Boss 核心被额外装甲包覆，需要更长时间持续输出。',
    color: '#67e8f9',
    riskLabel: 'Boss 最大耐久 ×1.25',
    badgeLabel: '装甲突破',
    hitPointMultiplier: 1.25,
    movementSpeedMultiplier: 1,
    incomingDamageMultiplier: 1,
    mechanicCadenceMultiplier: 1,
  },
  'kinetic-overclock': {
    id: 'kinetic-overclock',
    name: '动能超频',
    description: 'Boss 机动与专属机制同步超频，留给玩家的反应窗口更短。',
    color: '#f472b6',
    riskLabel: '移动 ×1.2，机制节奏 ×1.22',
    badgeLabel: '超频拦截',
    hitPointMultiplier: 1,
    movementSpeedMultiplier: 1.2,
    incomingDamageMultiplier: 1,
    mechanicCadenceMultiplier: 1.22,
  },
  'resonant-defense': {
    id: 'resonant-defense',
    name: '谐振防御',
    description: 'Boss 护层会分散每次命中的能量，要求更稳定的连续命中。',
    color: '#c084fc',
    riskLabel: 'Boss 承受伤害 ×0.76',
    badgeLabel: '谐振破除',
    hitPointMultiplier: 1,
    movementSpeedMultiplier: 1,
    incomingDamageMultiplier: 0.76,
    mechanicCadenceMultiplier: 1,
  },
};

export function createBossAffixPlan(
  runSeed: string,
  buildId: BuildId,
): Readonly<Record<10 | 20 | 30, BossAffixId>> {
  const affixIds = shuffleWithSeed(
    Object.keys(BOSS_AFFIX_DEFINITIONS) as BossAffixId[],
    `${runSeed}:build-mastery:${buildId}:boss-affix:v1`,
  );
  return {
    10: affixIds[0]!,
    20: affixIds[1]!,
    30: affixIds[2]!,
  };
}

export function getBossAffixDefinition(affixId: BossAffixId): BossAffixSnapshot {
  return BOSS_AFFIX_DEFINITIONS[affixId];
}

export function calculateBossAffixHitPoints(
  baseHitPoints: number,
  affix: BossAffixSnapshot | null,
): number {
  return Math.max(1, normalizePositiveValue(baseHitPoints) * (affix?.hitPointMultiplier ?? 1));
}

export function calculateBossAffixMovementSpeed(
  baseSpeed: number,
  affix: BossAffixSnapshot | null,
): number {
  return normalizePositiveValue(baseSpeed) * (affix?.movementSpeedMultiplier ?? 1);
}

export function calculateBossAffixIncomingDamage(
  baseDamage: number,
  affix: BossAffixSnapshot | null,
): number {
  return normalizePositiveValue(baseDamage) * (affix?.incomingDamageMultiplier ?? 1);
}

export function calculateBossAffixAdvanceMilliseconds(
  deltaMilliseconds: number,
  affix: BossAffixSnapshot | null,
): number {
  return normalizePositiveValue(deltaMilliseconds) * (affix?.mechanicCadenceMultiplier ?? 1);
}

export const ACTIVE_ABILITY_DEFINITIONS: Readonly<
  Record<ActiveAbilityId, ActiveAbilityDefinition>
> = {
  'nova-split': {
    id: 'nova-split',
    name: '裂变脉冲',
    description: '将当前弹丸立即裂变为 24 枚',
    shortcut: 'Q',
    cooldownMilliseconds: 12_000,
    durationMilliseconds: 0,
  },
  'aegis-field': {
    id: 'aegis-field',
    name: '护盾力场',
    description: '5 秒内反弹所有即将掉落的弹丸',
    shortcut: 'E',
    cooldownMilliseconds: 20_000,
    durationMilliseconds: 5_000,
  },
  'ion-pulse': {
    id: 'ion-pulse',
    name: '磁暴脉冲',
    description: '对全部砖块造成 1 点伤害，对首领造成 12 点伤害',
    shortcut: 'F',
    cooldownMilliseconds: 24_000,
    durationMilliseconds: 0,
  },
};

export const BRICK_DEFINITIONS: Readonly<Record<BrickKind, BrickDefinition>> = {
  normal: {
    id: 'normal',
    name: '基础砖块',
    description: '标准目标，耐久会随战役阶段稳定提升。',
    hitPoints: 1,
    score: 100,
    tint: 0x5eead4,
    secondaryDamageMultiplier: 1,
    rewardEffectId: null,
    rewardMagnitude: 0,
  },
  armored: {
    id: 'armored',
    name: '装甲砖块',
    description: '双层装甲，需要比基础砖块承受更多次命中。',
    hitPoints: 2,
    score: 180,
    tint: 0x818cf8,
    secondaryDamageMultiplier: 1,
    rewardEffectId: null,
    rewardMagnitude: 0,
  },
  reinforced: {
    id: 'reinforced',
    name: '强化砖块',
    description: '高耐久堡垒砖，是中后期阵列的主要承伤目标。',
    hitPoints: 5,
    score: 320,
    tint: 0x6366f1,
    secondaryDamageMultiplier: 1,
    rewardEffectId: null,
    rewardMagnitude: 0,
  },
  suppressor: {
    id: 'suppressor',
    name: '抑制砖块',
    description: '只承受 25% 的电弧、相位、爆炸和病毒等二次伤害。',
    hitPoints: 3,
    score: 280,
    tint: 0xc084fc,
    secondaryDamageMultiplier: 0.25,
    rewardEffectId: null,
    rewardMagnitude: 0,
  },
  replicator: {
    id: 'replicator',
    name: '复制砖块',
    description: '击碎后立即增加 1 枚弹丸。',
    hitPoints: 1,
    score: 140,
    tint: 0xfbbf24,
    secondaryDamageMultiplier: 1,
    rewardEffectId: 'spawn-projectile',
    rewardMagnitude: 1,
  },
  fission: {
    id: 'fission',
    name: '裂变砖块',
    description: '击碎后将当前弹丸直接裂变为 24 枚。',
    hitPoints: 2,
    score: 160,
    tint: 0xf472b6,
    secondaryDamageMultiplier: 1,
    rewardEffectId: 'split-projectile',
    rewardMagnitude: GAMEPLAY_CONFIG.projectiles.splitCount,
  },
};

export type BrickLayoutCell = BrickKind | null;

type BrickShapeDefinition = Readonly<{
  readonly id: string;
  readonly name: string;
  readonly pattern: readonly string[];
}>;

const BRICK_SHAPE_DEFINITIONS: readonly BrickShapeDefinition[] = [
  {
    id: 'full',
    name: '矩阵',
    pattern: ['1111111', '1111111', '1111111', '1111111', '1111111'],
  },
  {
    id: 'heart',
    name: '爱心',
    pattern: ['0011100', '0111110', '1111111', '1111111', '0111110'],
  },
  {
    id: 'face',
    name: '人脸',
    pattern: ['1111111', '1010101', '1110111', '1011101', '1111111'],
  },
  {
    id: 'tree',
    name: '树木',
    pattern: ['0001000', '0111110', '1111111', '0111110', '0011100'],
  },
  {
    id: 'diamond',
    name: '菱形',
    pattern: ['0011100', '0111110', '1111111', '0111110', '0011100'],
  },
  {
    id: 'spiral',
    name: '螺旋',
    pattern: ['1111110', '1100011', '0111110', '0000111', '0111110'],
  },
  {
    id: 'wave',
    name: '波浪',
    pattern: ['1110111', '0111011', '1110111', '0111011', '1110111'],
  },
  {
    id: 'ring',
    name: '圆环',
    pattern: ['0111110', '1011101', '1011101', '1011101', '0111110'],
  },
  {
    id: 'leaf',
    name: '树叶',
    pattern: ['0100010', '1111111', '0011100', '0111110', '1110111'],
  },
  {
    id: 'crown',
    name: '皇冠',
    pattern: ['1110111', '1111111', '1011101', '1111111', '0111110'],
  },
];

export interface WaveDifficultyProfile {
  readonly wave: number;
  readonly brickHitPointMultiplier: number;
  readonly shapeFillRatio: number;
  readonly armoredCount: number;
  readonly reinforcedCount: number;
  readonly suppressorCount: number;
  readonly replicatorCount: number;
  readonly fissionCount: number;
}

export interface BossEncounterDefinition {
  readonly wave: number;
  readonly tier: 1 | 2 | 3;
  readonly name: string;
  readonly maxHitPoints: number;
  readonly movementSpeedMultiplier: number;
  readonly verticalAmplitudeMultiplier: number;
  readonly shield: BossShieldMechanicDefinition | null;
  readonly summon: BossSummonMechanicDefinition | null;
  readonly singularity: BossSingularityMechanicDefinition | null;
}

export interface BossShieldMechanicDefinition {
  readonly id: 'aegis-nodes';
  readonly nodeCount: number;
  readonly nodeHitPoints: number;
  readonly vulnerableDurationMilliseconds: number;
  readonly nodeHorizontalOffset: number;
  readonly nodeVerticalOffset: number;
  readonly nodeSize: number;
}

export interface BossSummonMechanicDefinition {
  readonly id: 'swarm-uplink';
  readonly capacity: number;
  readonly reinforcementHitPoints: number;
  readonly initialDelayMilliseconds: number;
  readonly summonIntervalsByPhase: readonly [number, number, number];
  readonly bossDamageReductionPerReinforcement: number;
  readonly minimumBossDamageMultiplier: number;
  readonly reinforcementSize: number;
  readonly formationHorizontalOffset: number;
  readonly formationVerticalOffset: number;
}

export interface BossSingularityMechanicDefinition {
  readonly id: 'singularity-field';
  readonly warningDurationMilliseconds: number;
  readonly activeDurationMilliseconds: number;
  readonly cooldownMillisecondsByPhase: readonly [number, number, number];
  readonly steeringRadiansPerSecondByPhase: readonly [number, number, number];
  readonly aegisSteeringMultiplier: number;
  readonly anchorHorizontalOffset: number;
  readonly anchorY: number;
  readonly visualRadius: number;
}

export type EliteWaveDefinition = EliteWaveSnapshot;

export type EliteWaveEncounter = EliteWaveDefinition & {
  readonly wave: number;
};

export type RiskContractDefinition = Omit<RiskContractSnapshot, 'startWave' | 'endWave'>;

export type RunEventDefinition = Omit<
  RunEventSnapshot,
  'duplicateUpgradeId' | 'duplicateUpgradeMaximumStacks' | 'activatesCurse'
>;

export type RunCurseDefinition = Omit<RunCurseSnapshot, 'startWave' | 'endWave'>;

export type RunCleanseDefinition = Omit<
  RunCleanseSnapshot,
  'clearedWave' | 'remainingWaves' | 'removesRelicId' | 'curse'
>;

export const ELITE_WAVE_DEFINITIONS: Readonly<Record<EliteProtocolId, EliteWaveDefinition>> = {
  'fortress-matrix': {
    id: 'fortress-matrix',
    name: '堡垒矩阵',
    description: '精英砖块获得额外耐久，持续输出与目标优先级更加重要。',
    color: '#818cf8',
    riskLabel: '砖块耐久 +45%',
    rewardLabel: '得分 ×1.6 · 强化四选一',
    brickHitPointMultiplier: 1.45,
    projectileSpeedMultiplier: 1,
    secondaryDamageMultiplier: 1,
    scoreMultiplier: 1.6,
    rewardChoiceCount: 4,
  },
  'overclock-storm': {
    id: 'overclock-storm',
    name: '超频风暴',
    description: '弹丸飞行速度显著提高，挡板控制和护盾时机决定生存。',
    color: '#fb7185',
    riskLabel: '弹丸速度 +18%',
    rewardLabel: '得分 ×1.5 · 强化四选一',
    brickHitPointMultiplier: 1,
    projectileSpeedMultiplier: 1.18,
    secondaryDamageMultiplier: 1,
    scoreMultiplier: 1.5,
    rewardChoiceCount: 4,
  },
  'null-lockdown': {
    id: 'null-lockdown',
    name: '零域封锁',
    description: '电弧、相位、爆炸和病毒伤害被压制，直接命中价值提高。',
    color: '#c084fc',
    riskLabel: '球芯二次伤害 -50%',
    rewardLabel: '得分 ×1.8 · 强化四选一',
    brickHitPointMultiplier: 1,
    projectileSpeedMultiplier: 1,
    secondaryDamageMultiplier: 0.5,
    scoreMultiplier: 1.8,
    rewardChoiceCount: 4,
  },
};

export const RISK_CONTRACT_DEFINITIONS: Readonly<Record<RiskContractId, RiskContractDefinition>> = {
  'siege-bounty': {
    id: 'siege-bounty',
    name: '攻城悬赏',
    description: '整幕目标更加坚固，但每次奖励提供更多路线，适合稳定输出构筑。',
    color: '#fbbf24',
    riskLabel: '目标耐久 +25%',
    rewardLabel: '得分 ×1.4 · 奖励候选 +1',
    brickHitPointMultiplier: 1.25,
    projectileSpeedMultiplier: 1,
    directDamageMultiplier: 1,
    secondaryDamageMultiplier: 1,
    scoreMultiplier: 1.4,
    abilityCooldownRateMultiplier: 1,
    rewardChoiceBonus: 1,
  },
  'redline-license': {
    id: 'redline-license',
    name: '红线许可',
    description: '高速弹丸提高接球压力，技能同时更快恢复，鼓励主动控制节奏。',
    color: '#fb7185',
    riskLabel: '弹丸速度 +15%',
    rewardLabel: '得分 ×1.35 · 技能恢复 +25% · 候选 +1',
    brickHitPointMultiplier: 1,
    projectileSpeedMultiplier: 1.15,
    directDamageMultiplier: 1,
    secondaryDamageMultiplier: 1,
    scoreMultiplier: 1.35,
    abilityCooldownRateMultiplier: 1.25,
    rewardChoiceBonus: 1,
  },
  'null-dividend': {
    id: 'null-dividend',
    name: '零域红利',
    description: '球芯扩散伤害被削弱，直接命中获得增幅，要求重新安排目标优先级。',
    color: '#c084fc',
    riskLabel: '球芯二次伤害 -35%',
    rewardLabel: '直击伤害 +15% · 得分 ×1.5 · 候选 +1',
    brickHitPointMultiplier: 1,
    projectileSpeedMultiplier: 1,
    directDamageMultiplier: 1.15,
    secondaryDamageMultiplier: 0.65,
    scoreMultiplier: 1.5,
    abilityCooldownRateMultiplier: 1,
    rewardChoiceBonus: 1,
  },
};

export const RUN_EVENT_DEFINITIONS: Readonly<Record<RunEventId, RunEventDefinition>> = {
  'nanite-repair': {
    id: 'nanite-repair',
    name: '纳米维修舱',
    description: '用本局积分修复核心，保留生命应对后续高压关卡。',
    color: '#5eead4',
    costLabel: '消耗 1,200 分',
    rewardLabel: '修复 1 生命',
    scoreDelta: -1_200,
    lifeDelta: 1,
  },
  'vitality-exchange': {
    id: 'vitality-exchange',
    name: '生命承兑台',
    description: '抵押一格核心生命，将生存余量转换为本局积分。',
    color: '#fb7185',
    costLabel: '消耗 1 生命',
    rewardLabel: '获得 3,000 分',
    scoreDelta: 3_000,
    lifeDelta: -1,
  },
  'echo-forge': {
    id: 'echo-forge',
    name: '回声铸造厂',
    description: '锁定当前构筑中的一层强化并制造同频副本。',
    color: '#c084fc',
    costLabel: '消耗 1,800 分',
    rewardLabel: '复制 1 层已有强化',
    scoreDelta: -1_800,
    lifeDelta: 0,
  },
  'glass-overclock': {
    id: 'glass-overclock',
    name: '玻璃超频',
    description: '压缩挡板稳定性，换取三关高功率输出。',
    color: '#f472b6',
    costLabel: '后续 3 关 · 挡板宽度 -22%',
    rewardLabel: '后续 3 关 · 伤害 +30%',
    scoreDelta: 0,
    lifeDelta: 0,
  },
};

export const RUN_CURSE_DEFINITIONS: Readonly<Record<RunCurseId, RunCurseDefinition>> = {
  'glass-overclock': {
    id: 'glass-overclock',
    source: 'event',
    sourceRelicId: null,
    name: '玻璃超频',
    description: '挡板宽度下降 22%，全部伤害提高 30%，持续三个战斗关。',
    color: '#f472b6',
    riskLabel: '挡板宽度 -22%',
    rewardLabel: '伤害 +30%',
    damageMultiplier: 1.3,
    paddleWidthMultiplier: 0.78,
  },
  'reactor-instability': {
    id: 'reactor-instability',
    source: 'relic',
    sourceRelicId: 'cursed-reactor',
    name: '反应堆失稳',
    description: '咒蚀反应堆令挡板宽度降至 72%，持续三个普通战斗关。',
    color: '#fb7185',
    riskLabel: '挡板宽度 ×0.72',
    rewardLabel: '稳定后永久保留全伤害 ×1.65',
    damageMultiplier: 1,
    paddleWidthMultiplier: 0.72,
  },
};

export const RUN_CLEANSE_DEFINITION: RunCleanseDefinition = {
  id: 'stability-purge',
  name: '稳定净化站',
  description: '切断玻璃超频回路，正面增幅与负面压力会同时消失。',
  color: '#67e8f9',
  costLabel: '放弃余下伤害增幅',
  rewardLabel: '立即恢复完整挡板',
};

function normalizeCampaignWave(wave: number): number {
  const finiteWave = Number.isFinite(wave) ? Math.floor(wave) : 1;
  return Math.min(GAMEPLAY_CONFIG.run.maxWaves, Math.max(1, finiteWave));
}

export function isBossWave(wave: number): boolean {
  const safeWave = normalizeCampaignWave(wave);
  return GAMEPLAY_CONFIG.boss.waves.some((bossWave) => bossWave === safeWave);
}

export function isEliteWave(wave: number): boolean {
  const safeWave = normalizeCampaignWave(wave);
  return GAMEPLAY_CONFIG.elite.waves.some((eliteWave) => eliteWave === safeWave);
}

export function isRiskContractOfferWave(wave: number): boolean {
  const safeWave = normalizeCampaignWave(wave);
  return GAMEPLAY_CONFIG.contracts.offerWaves.some((offerWave) => offerWave === safeWave);
}

export function isRunEventOfferWave(wave: number): boolean {
  const safeWave = normalizeCampaignWave(wave);
  return GAMEPLAY_CONFIG.events.offerWaves.some((offerWave) => offerWave === safeWave);
}

export function isRunCleanseOfferWave(wave: number): boolean {
  const safeWave = normalizeCampaignWave(wave);
  return GAMEPLAY_CONFIG.cleanses.offerWaves.some((offerWave) => offerWave === safeWave);
}

export function createRunCleanseOffer(
  clearedWave: number,
  activeCurse: RunCurseSnapshot | null,
): RunCleanseSnapshot | null {
  const safeWave = normalizeCampaignWave(clearedWave);
  if (
    !activeCurse ||
    !isRunCleanseOfferWave(safeWave) ||
    safeWave !== activeCurse.startWave ||
    safeWave >= activeCurse.endWave
  ) {
    return null;
  }

  const remainingWaves = activeCurse.endWave - safeWave;
  const removesRelicId = activeCurse.source === 'relic' ? activeCurse.sourceRelicId : null;
  return {
    ...RUN_CLEANSE_DEFINITION,
    description: removesRelicId
      ? '切断失稳回路会同时摧毁来源遗物；保留则继续承担风险以锁定永久收益。'
      : RUN_CLEANSE_DEFINITION.description,
    costLabel: removesRelicId
      ? '移除「咒蚀反应堆」并失去全伤害 ×1.65'
      : `放弃余下 ${remainingWaves} 关${activeCurse.rewardLabel}`,
    clearedWave: safeWave,
    remainingWaves,
    removesRelicId,
    curse: { ...activeCurse },
  };
}

export function createRunEventOffer(
  runSeed: string,
  clearedWave: number,
  upgradeStacks: UpgradeStacks,
): RunEventSnapshot | null {
  const safeWave = normalizeCampaignWave(clearedWave);
  if (!isRunEventOfferWave(safeWave)) {
    return null;
  }

  const copyableUpgradeIds = UPGRADE_IDS.filter((upgradeId) => {
    const stack = upgradeStacks[upgradeId] ?? 0;
    const upgrade = UPGRADE_DEFINITIONS[upgradeId];
    return stack > 0 && stack < upgrade.maxStacks && upgrade.effectId !== 'grant-life';
  });
  const definitions = Object.values(RUN_EVENT_DEFINITIONS).filter(
    (definition) => definition.id !== 'echo-forge' || copyableUpgradeIds.length > 0,
  );
  const [definition] = shuffleWithSeed(definitions, `${runSeed}:run-event:${safeWave}:v2`);
  if (!definition) {
    throw new Error(`Run event selection failed after wave ${safeWave}`);
  }

  if (definition.id === 'glass-overclock') {
    return {
      ...definition,
      duplicateUpgradeId: null,
      duplicateUpgradeMaximumStacks: null,
      activatesCurse: {
        ...RUN_CURSE_DEFINITIONS['glass-overclock'],
        startWave: safeWave + 1,
        endWave: safeWave + 3,
      },
    };
  }

  if (definition.id !== 'echo-forge') {
    return {
      ...definition,
      duplicateUpgradeId: null,
      duplicateUpgradeMaximumStacks: null,
      activatesCurse: null,
    };
  }
  const [duplicateUpgradeId] = shuffleWithSeed(
    copyableUpgradeIds,
    `${runSeed}:run-event:${safeWave}:copy:v2`,
  );
  if (!duplicateUpgradeId) {
    throw new Error(`Run event copy target selection failed after wave ${safeWave}`);
  }
  const upgrade = UPGRADE_DEFINITIONS[duplicateUpgradeId];
  return {
    ...definition,
    rewardLabel: `复制「${upgrade.name}」1 层`,
    duplicateUpgradeId,
    duplicateUpgradeMaximumStacks: upgrade.maxStacks,
    activatesCurse: null,
  };
}

export function createRiskContractOffer(
  runSeed: string,
  clearedWave: number,
): RiskContractSnapshot | null {
  const safeWave = normalizeCampaignWave(clearedWave);
  if (!isRiskContractOfferWave(safeWave)) {
    return null;
  }

  const [contract] = shuffleWithSeed(
    Object.values(RISK_CONTRACT_DEFINITIONS),
    `${runSeed}:contract:${safeWave + 1}:v1`,
  );
  if (!contract) {
    throw new Error(`Risk contract selection failed after wave ${safeWave}`);
  }
  return {
    ...contract,
    startWave: safeWave + 1,
    endWave: Math.min(GAMEPLAY_CONFIG.run.maxWaves, safeWave + 10),
  };
}

function resolveActiveRiskContract(
  wave: number,
  contract: RiskContractSnapshot | null,
): RiskContractSnapshot | null {
  const safeWave = normalizeCampaignWave(wave);
  return contract && safeWave >= contract.startWave && safeWave <= contract.endWave
    ? contract
    : null;
}

function normalizePositiveValue(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function resolveActiveRunCurse(
  wave: number,
  curse: RunCurseSnapshot | null,
): RunCurseSnapshot | null {
  const safeWave = normalizeCampaignWave(wave);
  return curse && safeWave >= curse.startWave && safeWave <= curse.endWave ? curse : null;
}

export function calculateRunCurseAdjustedDamage(
  baseDamage: number,
  wave: number,
  curse: RunCurseSnapshot | null,
): number {
  return (
    normalizePositiveValue(baseDamage) * (resolveActiveRunCurse(wave, curse)?.damageMultiplier ?? 1)
  );
}

export function calculateRunCurseAdjustedPaddleWidth(
  baseWidth: number,
  wave: number,
  curse: RunCurseSnapshot | null,
): number {
  return (
    normalizePositiveValue(baseWidth) *
    (resolveActiveRunCurse(wave, curse)?.paddleWidthMultiplier ?? 1)
  );
}

export function calculateRiskContractBrickHitPoints(
  baseHitPoints: number,
  wave: number,
  contract: RiskContractSnapshot | null,
): number {
  return (
    normalizePositiveValue(baseHitPoints) *
    (resolveActiveRiskContract(wave, contract)?.brickHitPointMultiplier ?? 1)
  );
}

export function calculateRiskContractAdjustedProjectileSpeed(
  baseSpeed: number,
  wave: number,
  contract: RiskContractSnapshot | null,
): number {
  return (
    normalizePositiveValue(baseSpeed) *
    (resolveActiveRiskContract(wave, contract)?.projectileSpeedMultiplier ?? 1)
  );
}

export function calculateRiskContractAdjustedDamage(
  baseDamage: number,
  secondaryDamage: boolean,
  wave: number,
  contract: RiskContractSnapshot | null,
): number {
  const activeContract = resolveActiveRiskContract(wave, contract);
  const multiplier = secondaryDamage
    ? (activeContract?.secondaryDamageMultiplier ?? 1)
    : (activeContract?.directDamageMultiplier ?? 1);
  return normalizePositiveValue(baseDamage) * multiplier;
}

export function calculateRiskContractAdjustedScore(
  baseScore: number,
  wave: number,
  contract: RiskContractSnapshot | null,
): number {
  return Math.round(
    normalizePositiveValue(baseScore) *
      (resolveActiveRiskContract(wave, contract)?.scoreMultiplier ?? 1),
  );
}

export function calculateRiskContractAbilityCooldownRate(
  baseRate: number,
  wave: number,
  contract: RiskContractSnapshot | null,
): number {
  return (
    normalizePositiveValue(baseRate) *
    (resolveActiveRiskContract(wave, contract)?.abilityCooldownRateMultiplier ?? 1)
  );
}

export function calculateRiskContractRewardChoiceCount(
  baseCount: number,
  wave: number,
  contract: RiskContractSnapshot | null,
): number {
  const safeBaseCount = Number.isFinite(baseCount) ? Math.max(0, Math.floor(baseCount)) : 0;
  return safeBaseCount + (resolveActiveRiskContract(wave, contract)?.rewardChoiceBonus ?? 0);
}

export function createEliteWaveEncounter(runSeed: string, wave: number): EliteWaveEncounter | null {
  const safeWave = normalizeCampaignWave(wave);
  if (!isEliteWave(safeWave)) {
    return null;
  }

  const [protocol] = shuffleWithSeed(
    Object.values(ELITE_WAVE_DEFINITIONS),
    `${runSeed}:elite:${safeWave}:v1`,
  );
  if (!protocol) {
    throw new Error(`Elite protocol selection failed for wave ${safeWave}`);
  }
  return { ...protocol, wave: safeWave };
}

export function calculateEliteBrickHitPoints(
  kind: BrickKind,
  wave: number,
  elite: EliteWaveDefinition | null,
): number {
  return getBrickHitPoints(kind, wave) * (elite?.brickHitPointMultiplier ?? 1);
}

export function calculateEliteAdjustedProjectileSpeed(
  baseSpeed: number,
  elite: EliteWaveDefinition | null,
): number {
  const safeSpeed = Number.isFinite(baseSpeed) ? Math.max(0, baseSpeed) : 0;
  return safeSpeed * (elite?.projectileSpeedMultiplier ?? 1);
}

export function calculateEliteAdjustedDamage(
  baseDamage: number,
  secondaryDamage: boolean,
  elite: EliteWaveDefinition | null,
): number {
  const safeDamage = Number.isFinite(baseDamage) ? Math.max(0, baseDamage) : 0;
  return safeDamage * (secondaryDamage ? (elite?.secondaryDamageMultiplier ?? 1) : 1);
}

export function calculateEliteAdjustedScore(
  baseScore: number,
  elite: EliteWaveDefinition | null,
): number {
  const safeScore = Number.isFinite(baseScore) ? Math.max(0, baseScore) : 0;
  return Math.round(safeScore * (elite?.scoreMultiplier ?? 1));
}

export function getBossEncounterDefinition(wave: number): BossEncounterDefinition | null {
  const safeWave = normalizeCampaignWave(wave);
  const tierIndex = GAMEPLAY_CONFIG.boss.waves.findIndex((bossWave) => bossWave === safeWave);
  if (tierIndex < 0) {
    return null;
  }

  return {
    wave: safeWave,
    tier: (tierIndex + 1) as 1 | 2 | 3,
    name: tierIndex === 0 ? '棱镜哨卫' : tierIndex === 1 ? '裂群主机' : BOSS_DEFINITION.name,
    maxHitPoints: GAMEPLAY_CONFIG.boss.hitPointsByTier[tierIndex]!,
    movementSpeedMultiplier: GAMEPLAY_CONFIG.boss.movementSpeedMultipliers[tierIndex]!,
    verticalAmplitudeMultiplier: GAMEPLAY_CONFIG.boss.verticalAmplitudeMultipliers[tierIndex]!,
    shield:
      tierIndex === 0 ? { id: 'aegis-nodes', ...GAMEPLAY_CONFIG.boss.firstEncounterShield } : null,
    summon:
      tierIndex === 1
        ? { id: 'swarm-uplink', ...GAMEPLAY_CONFIG.boss.secondEncounterSummon }
        : null,
    singularity:
      tierIndex === 2
        ? { id: 'singularity-field', ...GAMEPLAY_CONFIG.boss.thirdEncounterSingularity }
        : null,
  };
}

export function createWaveDifficultyProfile(wave: number): WaveDifficultyProfile {
  const safeWave = normalizeCampaignWave(wave);
  const brickHitPointMultiplier =
    safeWave <= 9
      ? 1 + (safeWave - 1) * 0.06
      : safeWave <= 19
        ? 1.9 + (safeWave - 11) * 0.11
        : 3.3 + (safeWave - 21) * 0.2;

  return {
    wave: safeWave,
    brickHitPointMultiplier,
    shapeFillRatio: Math.min(1.5, 1.08 + safeWave * 0.014),
    armoredCount: 3 + Math.floor((safeWave - 1) / 3),
    reinforcedCount: safeWave < 6 ? 0 : 1 + Math.floor((safeWave - 6) / 3),
    suppressorCount: safeWave < 11 ? 0 : 1 + Math.floor((safeWave - 11) / 3),
    replicatorCount: safeWave <= 9 ? 5 : safeWave <= 19 ? 3 : 2,
    fissionCount: safeWave <= 19 ? 2 : 1,
  };
}

export function getBrickHitPoints(kind: BrickKind, wave: number): number {
  return (
    BRICK_DEFINITIONS[kind].hitPoints * createWaveDifficultyProfile(wave).brickHitPointMultiplier
  );
}

export function calculateBrickDamageAfterResistance(
  kind: BrickKind,
  requestedDamage: number,
  secondaryCoreDamage: boolean,
): number {
  const safeDamage = Number.isFinite(requestedDamage) ? Math.max(0, requestedDamage) : 0;
  return safeDamage * (secondaryCoreDamage ? BRICK_DEFINITIONS[kind].secondaryDamageMultiplier : 1);
}

export const UPGRADE_DEFINITIONS: Readonly<Record<UpgradeId, UpgradeDefinition>> = {
  'echo-chamber': {
    id: 'echo-chamber',
    name: '回声舱',
    description: '复制砖块额外生成 1 枚弹丸',
    effectId: 'increase-spawn-count',
    magnitude: 1,
    maxStacks: 4,
    category: 'foundation',
  },
  'fission-matrix': {
    id: 'fission-matrix',
    name: '裂变矩阵',
    description: '裂变额外生成 8 枚弹丸',
    effectId: 'increase-split-count',
    magnitude: 8,
    maxStacks: 3,
    category: 'foundation',
  },
  'kinetic-core': {
    id: 'kinetic-core',
    name: '动能核心',
    description: '弹丸伤害提升 25%',
    effectId: 'increase-damage',
    magnitude: 0.25,
    maxStacks: 5,
    category: 'foundation',
  },
  'overclock-drive': {
    id: 'overclock-drive',
    name: '超频驱动',
    description: '弹丸速度提升 8%',
    effectId: 'increase-speed',
    magnitude: 0.08,
    maxStacks: 4,
    category: 'foundation',
  },
  'wide-guard': {
    id: 'wide-guard',
    name: '宽域护板',
    description: '挡板宽度提升 18%',
    effectId: 'expand-paddle',
    magnitude: 0.18,
    maxStacks: 4,
    category: 'foundation',
  },
  'reserve-core': {
    id: 'reserve-core',
    name: '备用核心',
    description: '立即补充 1 点生命，上限为 5',
    effectId: 'grant-life',
    magnitude: 1,
    maxStacks: 2,
    category: 'foundation',
  },
  'arc-core': {
    id: 'arc-core',
    name: '电弧球芯',
    description: '命中砖块时电击 1 个附近目标，造成 35% 伤害',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 1,
    category: 'ball-core',
    ballCoreId: 'arc',
  },
  'arc-conductor': {
    id: 'arc-conductor',
    name: '超导阵列',
    description: '电弧额外跳跃 1 个目标',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 2,
    category: 'core-amplifier',
    ballCoreId: 'arc',
    prerequisiteUpgradeIds: ['arc-core'],
  },
  'phase-core': {
    id: 'phase-core',
    name: '相位球芯',
    description: '命中时贯穿同列后方砖块，造成 55% 伤害',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 1,
    category: 'ball-core',
    ballCoreId: 'phase',
  },
  'phase-lance': {
    id: 'phase-lance',
    name: '相位长枪',
    description: '贯穿额外延伸 1 个目标',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 2,
    category: 'core-amplifier',
    ballCoreId: 'phase',
    prerequisiteUpgradeIds: ['phase-core'],
  },
  'blast-core': {
    id: 'blast-core',
    name: '爆破球芯',
    description: '摧毁砖块时引爆周围目标，造成 45% 伤害',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 1,
    category: 'ball-core',
    ballCoreId: 'blast',
  },
  'blast-shaping': {
    id: 'blast-shaping',
    name: '爆轰塑形',
    description: '扩大爆炸范围并提高爆炸伤害',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 2,
    category: 'core-amplifier',
    ballCoreId: 'blast',
    prerequisiteUpgradeIds: ['blast-core'],
  },
  'corrosion-core': {
    id: 'corrosion-core',
    name: '腐蚀球芯',
    description: '连续命中同一目标会叠加易伤',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 1,
    category: 'ball-core',
    ballCoreId: 'corrosion',
  },
  'corrosion-catalyst': {
    id: 'corrosion-catalyst',
    name: '腐蚀催化剂',
    description: '提高每层腐蚀易伤，最多叠加 4 层',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 2,
    category: 'core-amplifier',
    ballCoreId: 'corrosion',
    prerequisiteUpgradeIds: ['corrosion-core'],
  },
  'gravity-core': {
    id: 'gravity-core',
    name: '引力球芯',
    description: '弹丸会轻微追踪场上的高价值目标',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 1,
    category: 'ball-core',
    ballCoreId: 'gravity',
  },
  'gravity-lens': {
    id: 'gravity-lens',
    name: '引力透镜',
    description: '提高追踪转向强度',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 2,
    category: 'core-amplifier',
    ballCoreId: 'gravity',
    prerequisiteUpgradeIds: ['gravity-core'],
  },
  'rebound-core': {
    id: 'rebound-core',
    name: '回弹球芯',
    description: '每次挡板回弹为下一次命中蓄能 25%',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 1,
    category: 'ball-core',
    ballCoreId: 'rebound',
  },
  'rebound-capacitor': {
    id: 'rebound-capacitor',
    name: '回弹电容',
    description: '提高回弹蓄能，最多积累 3 层',
    effectId: 'configure-ball-core',
    magnitude: 1,
    maxStacks: 2,
    category: 'core-amplifier',
    ballCoreId: 'rebound',
    prerequisiteUpgradeIds: ['rebound-core'],
  },
};

export const BALL_CORE_DEFINITIONS: Readonly<Record<BallCoreId, BallCoreDefinition>> = {
  arc: { id: 'arc', name: '电弧', color: '#67e8f9', upgradeId: 'arc-core' },
  phase: { id: 'phase', name: '相位', color: '#a78bfa', upgradeId: 'phase-core' },
  blast: { id: 'blast', name: '爆破', color: '#fb7185', upgradeId: 'blast-core' },
  corrosion: { id: 'corrosion', name: '腐蚀', color: '#a3e635', upgradeId: 'corrosion-core' },
  gravity: { id: 'gravity', name: '引力', color: '#c084fc', upgradeId: 'gravity-core' },
  rebound: { id: 'rebound', name: '回弹', color: '#fbbf24', upgradeId: 'rebound-core' },
};

export const BALL_FUSION_DEFINITIONS: Readonly<Record<BallFusionId, BallFusionDefinition>> = {
  'rail-storm': {
    id: 'rail-storm',
    name: '磁轨雷暴',
    description: '相位贯穿会继续触发电弧跳跃',
    requiredCoreIds: ['arc', 'phase'],
  },
  thunderburst: {
    id: 'thunderburst',
    name: '雷霆爆轰',
    description: '爆炸命中会继续触发电弧跳跃',
    requiredCoreIds: ['arc', 'blast'],
  },
  'toxic-lance': {
    id: 'toxic-lance',
    name: '蚀毒长枪',
    description: '相位贯穿会为后方目标叠加腐蚀',
    requiredCoreIds: ['phase', 'corrosion'],
  },
  'singularity-bomb': {
    id: 'singularity-bomb',
    name: '奇点炸弹',
    description: '引力压缩爆炸，显著扩大范围与伤害',
    requiredCoreIds: ['blast', 'gravity'],
  },
  'orbital-return': {
    id: 'orbital-return',
    name: '轨道回归',
    description: '回弹后获得更强追踪与额外蓄能',
    requiredCoreIds: ['gravity', 'rebound'],
  },
  'viral-echo': {
    id: 'viral-echo',
    name: '病毒回声',
    description: '蓄能命中会将腐蚀扩散至邻近目标',
    requiredCoreIds: ['corrosion', 'rebound'],
  },
};

export interface BallCoreDefinition {
  readonly id: BallCoreId;
  readonly name: string;
  readonly color: string;
  readonly upgradeId: UpgradeId;
}

export const RELIC_DEFINITIONS: Readonly<Record<RelicId, RelicDefinition>> = {
  'prism-lattice': {
    id: 'prism-lattice',
    name: '棱镜晶格',
    description: '弹丸基础伤害提升 35%',
    color: '#67e8f9',
  },
  'kinetic-anchor': {
    id: 'kinetic-anchor',
    name: '动能锚',
    description: '弹丸速度降低 10%，伤害提升 55%',
    color: '#fbbf24',
  },
  'overdrive-coil': {
    id: 'overdrive-coil',
    name: '过载线圈',
    description: '弹丸速度提升 22%',
    color: '#fb7185',
  },
  'guardian-alloy': {
    id: 'guardian-alloy',
    name: '守卫合金',
    description: '挡板宽度提升 30%',
    color: '#5eead4',
  },
  'reserve-heart': {
    id: 'reserve-heart',
    name: '备用心脏',
    description: '获得时修复 2 点生命，并将生命上限提升至 6',
    color: '#f472b6',
  },
  'harvest-lens': {
    id: 'harvest-lens',
    name: '收割透镜',
    description: '摧毁砖块额外获得 45% 分数',
    color: '#a3e635',
  },
  'duplication-seal': {
    id: 'duplication-seal',
    name: '复制印记',
    description: '复制砖额外生成 2 枚弹丸',
    color: '#fde047',
  },
  'fractal-chamber': {
    id: 'fractal-chamber',
    name: '分形腔体',
    description: '裂变额外生成 12 枚弹丸',
    color: '#c084fc',
  },
  'chronon-battery': {
    id: 'chronon-battery',
    name: '时序电池',
    description: '主动技能冷却速度提升 25%',
    color: '#818cf8',
  },
  'aegis-seed': {
    id: 'aegis-seed',
    name: '护盾种子',
    description: '护盾力场持续时间延长 2 秒',
    color: '#2dd4bf',
  },
  'echo-counter': {
    id: 'echo-counter',
    name: '回声计数器',
    description: '每摧毁 12 块砖自动复制 1 枚弹丸',
    color: '#38bdf8',
  },
  'singularity-crown': {
    id: 'singularity-crown',
    name: '奇点王冠',
    description: '所有球芯二次伤害提升 30%',
    color: '#d8b4fe',
  },
  'cursed-reactor': {
    id: 'cursed-reactor',
    name: '咒蚀反应堆',
    description: '持有时全伤害 ×1.65；后续三关挡板缩至 72%，净化会移除遗物',
    color: '#fb7185',
    curseId: 'reactor-instability',
    rewardLabel: '永久全伤害 ×1.65',
    riskLabel: '后续 3 关挡板 ×0.72；净化会移除遗物',
  },
};

export function createRelicCurse(relicId: RelicId, clearedWave: number): RunCurseSnapshot | null {
  const safeWave = normalizeCampaignWave(clearedWave);
  if (
    relicId !== 'cursed-reactor' ||
    !GAMEPLAY_CONFIG.boss.rewardWaves.some((rewardWave) => rewardWave === safeWave)
  ) {
    return null;
  }
  return {
    ...RUN_CURSE_DEFINITIONS['reactor-instability'],
    startWave: safeWave + 1,
    endWave: safeWave + 3,
  };
}

export function calculateCursedRelicAdjustedDamage(
  baseDamage: number,
  activeRelicIds: readonly RelicId[],
): number {
  return (
    normalizePositiveValue(baseDamage) * (activeRelicIds.includes('cursed-reactor') ? 1.65 : 1)
  );
}

export function createRelicChoices(
  runSeed: string,
  clearedWave: number,
  activeRelicIds: readonly RelicId[],
  rerollIndex = 0,
  activeCurse: RunCurseSnapshot | null = null,
): RelicId[] {
  const safeWave = normalizeCampaignWave(clearedWave);
  const activeRelics = new Set(activeRelicIds);
  const eligibleRelicIds = (Object.keys(RELIC_DEFINITIONS) as RelicId[]).filter(
    (relicId) =>
      !activeRelics.has(relicId) && (relicId !== 'cursed-reactor' || activeCurse === null),
  );
  const shuffled = shuffleWithSeed(
    eligibleRelicIds,
    `${runSeed}:relic:${safeWave}:reroll:${Math.max(0, Math.floor(rerollIndex))}:v2`,
  );
  if (
    GAMEPLAY_CONFIG.boss.rewardWaves.some((rewardWave) => rewardWave === safeWave) &&
    eligibleRelicIds.includes('cursed-reactor')
  ) {
    const guaranteedChoices: RelicId[] = [
      'cursed-reactor',
      ...shuffled.filter((relicId) => relicId !== 'cursed-reactor'),
    ];
    return guaranteedChoices.slice(0, GAMEPLAY_CONFIG.run.upgradeChoiceCount);
  }
  return shuffled.slice(0, GAMEPLAY_CONFIG.run.upgradeChoiceCount);
}

export interface BallFusionDefinition {
  readonly id: BallFusionId;
  readonly name: string;
  readonly description: string;
  readonly requiredCoreIds: readonly [BallCoreId, BallCoreId];
}

export interface BallCoreCombatProfile {
  readonly activeCoreIds: readonly BallCoreId[];
  readonly activeFusionIds: readonly BallFusionId[];
  readonly arcTargets: number;
  readonly arcDamageRatio: number;
  readonly phaseTargets: number;
  readonly phaseDamageRatio: number;
  readonly blastRadius: number;
  readonly blastDamageRatio: number;
  readonly corrosionDamagePerStack: number;
  readonly gravitySteeringRadiansPerSecond: number;
  readonly reboundDamagePerCharge: number;
  readonly reboundMaximumCharges: number;
}

export function resolveActiveBallCoreIds(upgradeStacks: UpgradeStacks): BallCoreId[] {
  return Object.values(BALL_CORE_DEFINITIONS)
    .filter((core) => (upgradeStacks[core.upgradeId] ?? 0) > 0)
    .map((core) => core.id);
}

export function resolveActiveBallFusionIds(upgradeStacks: UpgradeStacks): BallFusionId[] {
  const activeCoreIds = new Set(resolveActiveBallCoreIds(upgradeStacks));
  return Object.values(BALL_FUSION_DEFINITIONS)
    .filter((fusion) => fusion.requiredCoreIds.every((coreId) => activeCoreIds.has(coreId)))
    .map((fusion) => fusion.id);
}

export function createBallCoreCombatProfile(upgradeStacks: UpgradeStacks): BallCoreCombatProfile {
  const activeCoreIds = resolveActiveBallCoreIds(upgradeStacks);
  const activeFusionIds = resolveActiveBallFusionIds(upgradeStacks);
  const activeCores = new Set(activeCoreIds);
  const activeFusions = new Set(activeFusionIds);
  const arcAmplifier = upgradeStacks['arc-conductor'] ?? 0;
  const phaseAmplifier = upgradeStacks['phase-lance'] ?? 0;
  const blastAmplifier = upgradeStacks['blast-shaping'] ?? 0;
  const corrosionAmplifier = upgradeStacks['corrosion-catalyst'] ?? 0;
  const gravityAmplifier = upgradeStacks['gravity-lens'] ?? 0;
  const reboundAmplifier = upgradeStacks['rebound-capacitor'] ?? 0;

  return {
    activeCoreIds,
    activeFusionIds,
    arcTargets: activeCores.has('arc') ? 1 + arcAmplifier : 0,
    arcDamageRatio: activeFusions.has('rail-storm') ? 0.42 : 0.35,
    phaseTargets: activeCores.has('phase') ? 1 + phaseAmplifier : 0,
    phaseDamageRatio: activeFusions.has('toxic-lance') ? 0.65 : 0.55,
    blastRadius: activeCores.has('blast')
      ? 104 + blastAmplifier * 24 + (activeFusions.has('singularity-bomb') ? 42 : 0)
      : 0,
    blastDamageRatio: activeFusions.has('singularity-bomb') ? 0.72 : 0.45 + blastAmplifier * 0.1,
    corrosionDamagePerStack: activeCores.has('corrosion') ? 0.18 + corrosionAmplifier * 0.09 : 0,
    gravitySteeringRadiansPerSecond: activeCores.has('gravity')
      ? 0.32 + gravityAmplifier * 0.12 + (activeFusions.has('orbital-return') ? 0.16 : 0)
      : 0,
    reboundDamagePerCharge: activeCores.has('rebound')
      ? 0.25 + reboundAmplifier * 0.15 + (activeFusions.has('orbital-return') ? 0.2 : 0)
      : 0,
    reboundMaximumCharges: activeCores.has('rebound') ? 3 : 0,
  };
}

export const BUILD_DEFINITIONS: Readonly<Record<BuildId, BuildDefinition>> = {
  swarm: {
    id: 'swarm',
    name: '群蜂',
    description: '快速复制弹丸，用数量铺满战场',
    upgradeIds: ['echo-chamber', 'overclock-drive'],
  },
  burst: {
    id: 'burst',
    name: '裂变',
    description: '强化伤害与分裂规模，追求瞬间爆发',
    upgradeIds: ['fission-matrix', 'kinetic-core'],
  },
  fortress: {
    id: 'fortress',
    name: '堡垒',
    description: '扩大挡板并增加生命，容错更高',
    upgradeIds: ['wide-guard', 'reserve-core'],
  },
};

export const DEFAULT_BUILD_ID: BuildId = 'swarm';

export const EFFECT_LABELS: Readonly<Record<CombatEffectId, string>> = {
  'spawn-projectile': '弹丸复制',
  'split-projectile': '24 向裂变',
  'increase-spawn-count': '复制强化',
  'increase-split-count': '裂变强化',
  'increase-damage': '动能强化',
  'increase-speed': '速度强化',
  'expand-paddle': '挡板扩展',
  'grant-life': '生命补给',
  'configure-ball-core': '球芯共鸣',
};

const UPGRADE_IDS = Object.keys(UPGRADE_DEFINITIONS) as UpgradeId[];
const MAXIMUM_UPGRADE_STACKS = Object.fromEntries(
  UPGRADE_IDS.map((upgradeId) => [upgradeId, UPGRADE_DEFINITIONS[upgradeId].maxStacks]),
) as Readonly<Record<UpgradeId, number>>;

export function createUpgradeChoices(
  runSeed: string,
  clearedWave: number,
  upgradeStacks: UpgradeStacks,
  rerollIndex = 0,
  choiceCount: number = GAMEPLAY_CONFIG.run.upgradeChoiceCount,
): UpgradeId[] {
  const eligibleUpgradeIds = UPGRADE_IDS.filter((upgradeId) =>
    (UPGRADE_DEFINITIONS[upgradeId].prerequisiteUpgradeIds ?? []).every(
      (prerequisiteId) => (upgradeStacks[prerequisiteId] ?? 0) > 0,
    ),
  );
  return selectSeededUpgradeChoices({
    runSeed,
    clearedWave,
    upgradeIds: eligibleUpgradeIds,
    upgradeStacks,
    maximumStacks: MAXIMUM_UPGRADE_STACKS,
    count: Math.max(0, Math.floor(choiceCount)),
    rerollIndex,
  });
}

function shapePatternToSlots(shape: BrickShapeDefinition): readonly number[] {
  const columns = GAMEPLAY_CONFIG.bricks.columns;
  const rows = GAMEPLAY_CONFIG.bricks.rows;
  if (shape.pattern.length !== rows) {
    throw new Error(`Invalid brick shape pattern row count: ${shape.id}`);
  }
  return shape.pattern.flatMap((row, rowIndex) => {
    if (row.length !== columns) {
      throw new Error(`Invalid brick shape pattern column count: ${shape.id}`);
    }
    return row
      .split('')
      .flatMap((character, columnIndex) =>
        character === '1' ? [rowIndex * columns + columnIndex] : [],
      );
  });
}

function chooseBrickShapeSlots(runSeed: string, wave: number): readonly number[] {
  const safeWave = normalizeCampaignWave(wave);
  const [shape] = shuffleWithSeed(BRICK_SHAPE_DEFINITIONS, `${runSeed}:shape:${safeWave}:v1`);
  if (!shape) {
    throw new Error(`Brick shape selection failed for wave ${safeWave}`);
  }
  return shapePatternToSlots(shape);
}

export function createWaveLayout(runSeed: string, wave: number): BrickLayoutCell[][] {
  const difficulty = createWaveDifficultyProfile(wave);
  const safeWave = difficulty.wave;
  const totalBricks = GAMEPLAY_CONFIG.bricks.rows * GAMEPLAY_CONFIG.bricks.columns;
  const brickSlots = chooseBrickShapeSlots(runSeed, safeWave);
  const directLaunchIndex =
    (GAMEPLAY_CONFIG.bricks.rows - 1) * GAMEPLAY_CONFIG.bricks.columns +
    Math.floor(GAMEPLAY_CONFIG.bricks.columns / 2);
  const slotSet = new Set<number>(brickSlots);
  slotSet.add(directLaunchIndex);
  let slotSequence = [...slotSet];
  const shapeSlotCapacity = slotSequence.length;
  const targetSlotCapacity = Math.min(
    totalBricks,
    Math.floor(shapeSlotCapacity * difficulty.shapeFillRatio),
  );
  if (targetSlotCapacity > shapeSlotCapacity) {
    const additionalSlots = [...Array(totalBricks).keys()].filter(
      (slotIndex) => !slotSet.has(slotIndex),
    );
    const extraSlots = shuffleWithSeed(
      additionalSlots,
      `${runSeed}:shape-fill:${safeWave}:v1`,
    ).slice(0, targetSlotCapacity - shapeSlotCapacity);
    slotSequence = [...slotSequence, ...extraSlots];
  }
  let slotsRemaining = targetSlotCapacity;
  const allocate = (requestedCount: number) => {
    const allocatedCount = Math.min(slotsRemaining, Math.max(0, Math.floor(requestedCount)));
    slotsRemaining -= allocatedCount;
    return allocatedCount;
  };
  const fission = allocate(difficulty.fissionCount);
  const replicator = allocate(difficulty.replicatorCount);
  const suppressor = allocate(difficulty.suppressorCount);
  const reinforced = allocate(difficulty.reinforcedCount);
  const armored = allocate(difficulty.armoredCount);
  const normalCount = slotsRemaining;
  const brickKinds: BrickKind[] = [
    ...Array<BrickKind>(normalCount).fill('normal'),
    ...Array<BrickKind>(armored).fill('armored'),
    ...Array<BrickKind>(reinforced).fill('reinforced'),
    ...Array<BrickKind>(suppressor).fill('suppressor'),
    ...Array<BrickKind>(replicator).fill('replicator'),
    ...Array<BrickKind>(fission).fill('fission'),
  ];
  const slotLayout = new Array<BrickLayoutCell>(totalBricks).fill(null);
  const shuffled = shuffleWithSeed(brickKinds, `${runSeed}:wave:${safeWave}`);
  if (shuffled.length !== slotSequence.length) {
    throw new Error(
      `Brick layout shape mismatch for wave ${safeWave}: expected ${slotSequence.length}, got ${shuffled.length}`,
    );
  }
  slotSequence.forEach((slotIndex, sequenceIndex) => {
    slotLayout[slotIndex] = shuffled[sequenceIndex]!;
  });

  const directLaunchBrick = slotLayout[directLaunchIndex];
  if (directLaunchBrick == null) {
    throw new Error(
      `Brick layout missing launch slot at index ${directLaunchIndex} for wave ${safeWave}`,
    );
  }
  return Array.from({ length: GAMEPLAY_CONFIG.bricks.rows }, (_, row) =>
    slotLayout.slice(
      row * GAMEPLAY_CONFIG.bricks.columns,
      (row + 1) * GAMEPLAY_CONFIG.bricks.columns,
    ),
  );
}

export const BRICK_LAYOUT: readonly (readonly BrickLayoutCell[])[] = createWaveLayout(
  'M4-PREVIEW',
  1,
);
import {
  selectSeededUpgradeChoices,
  shuffleWithSeed,
  type ActiveAbilityDefinition,
  type ActiveAbilityId,
  type BallCoreId,
  type BallFusionId,
  type BossDefinition,
  type BossAffixId,
  type BossAffixSnapshot,
  type BrickDefinition,
  type BrickKind,
  type BuildDefinition,
  type BuildId,
  type CombatEffectId,
  type EliteProtocolId,
  type EliteWaveSnapshot,
  type ObjectiveDefinition,
  type ObjectiveId,
  type RelicDefinition,
  type RelicId,
  type RiskContractId,
  type RiskContractSnapshot,
  type RunCleanseSnapshot,
  type RunCurseId,
  type RunCurseSnapshot,
  type RunEventId,
  type RunEventSnapshot,
  type SeasonChallengeDefinition,
  type SeasonChallengeId,
  type SeasonDefinition,
  type SeasonId,
  type UpgradeDefinition,
  type UpgradeId,
  type UpgradeStacks,
} from '@brick-rogue/game-core';
